import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Download,
  Edit,
  Trash2,
  Eye,
  DollarSign,
  AlertCircle,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  FileCheck,
  Link as LinkIcon,
  Check,
  Copy,
  HelpCircle,
} from 'lucide-react';
import Layout from '../components/Layout';
import { useCompany } from '../context/CompanyContext';
import { useI18n } from '../hooks/useI18n';
import { useOnboardingTour } from '../hooks/useOnboardingTour';
import { invoiceAPI, templateAPI, clientAPI } from '../services/api';
import { Invoice, PaymentMethod, InvoiceType, Currency, InvoiceSearchResponse } from '../types';
import InvoiceTypeModal from '../components/InvoiceTypeModal';
import AddPaymentModal from '../components/AddPaymentModal';
import { setEncryptionEnabled } from '../utils/EncryptionUtils';
import { supplierAPI } from "../services/supplierApi";

const InvoiceListPage: React.FC = () => {
  // Force rebuild check
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const { t, isRTL } = useI18n();
  const { startTour, manualStart } = useOnboardingTour();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | ''>('');
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<InvoiceType | ''>('');
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | ''>('');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [invoiceTypes, setInvoiceTypes] = useState<InvoiceType[]>([]);
  const [showInvoiceTypeModal, setShowInvoiceTypeModal] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState<string | null>(null);
  const [viewingPDF, setViewingPDF] = useState<string | null>(null);
  const [copyingLink, setCopyingLink] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  // New filters state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startDeadlineDate, setStartDeadlineDate] = useState('');
  const [endDeadlineDate, setEndDeadlineDate] = useState('');
  const [searchInvoiceNumber, setSearchInvoiceNumber] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clients, setClients] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreTriggerRef, setLoadMoreTriggerRef] = useState<HTMLDivElement | null>(null);


  // Load initial data
  useEffect(() => {
    if (selectedCompany.selectedCompanyId && selectedCompany.selectedCompany) {
      // Ensure encryption is enabled if the company has it
      setEncryptionEnabled(!!selectedCompany.selectedCompany.encryptionFeatureEnabled);
      loadInvoices();
      loadPaymentMethods();
      loadInvoiceTypes();
      loadClientsAndSuppliers();
    }
  }, [selectedCompany.selectedCompanyId, selectedCompany.selectedCompany]);

  // Auto-start onboarding tour on first visit
  useEffect(() => {
    if (selectedCompany.selectedCompanyId) {
      startTour({
        tourId: 'invoice-list',
        steps: [
          {
            element: '#invoice-list-create-btn',
            popover: {
              title: t('onboarding.invoiceList.createBtnTitle'),
              description: t('onboarding.invoiceList.createBtnDesc'),
              side: 'bottom' as const,
              align: 'end' as const,
            },
          },
          {
            element: '#invoice-list-search-bar',
            popover: {
              title: t('onboarding.invoiceList.searchTitle'),
              description: t('onboarding.invoiceList.searchDesc'),
              side: 'bottom' as const,
              align: 'start' as const,
            },
          },
          {
            element: '#invoice-list-table',
            popover: {
              title: t('onboarding.invoiceList.tableTitle'),
              description: t('onboarding.invoiceList.tableDesc'),
              side: 'top' as const,
              align: 'start' as const,
            },
          },
        ],
        autoStartDelay: 800,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany.selectedCompanyId]);

  // Effect for loading invoices when page changes (infinite scroll)
  useEffect(() => {
    if (selectedCompany.selectedCompanyId && currentPage > 0) {
      loadMoreInvoices();
    }
  }, [currentPage]);

  // Initial load
  useEffect(() => {
    if (selectedCompany.selectedCompanyId) {
      loadInvoices();
    }
  }, [selectedCompany.selectedCompanyId]);

  // Auto-scroll infinite loading with Intersection Observer
  useEffect(() => {
    if (!loadMoreTriggerRef || !hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          setCurrentPage(prev => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreTriggerRef);

    return () => {
      if (loadMoreTriggerRef) {
        observer.unobserve(loadMoreTriggerRef);
      }
    };
  }, [loadMoreTriggerRef, hasMore, isLoadingMore]);

  const loadClientsAndSuppliers = async () => {
    if (!selectedCompany.selectedCompanyId) return;
    const fetchedClients = await clientAPI.getClientsByCompany(selectedCompany.selectedCompanyId);
    setClients(fetchedClients);
    const suppliersData = await supplierAPI.getSuppliersByCompany(selectedCompany.selectedCompanyId);
    setSuppliers(suppliersData);
  };

  const search = async (page: number): Promise<InvoiceSearchResponse> => {
    return await invoiceAPI.searchInvoicesAdvanced({
      companyId: selectedCompany.selectedCompanyId || '',
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      startDeadlineDate: startDeadlineDate || undefined,
      endDeadlineDate: endDeadlineDate || undefined,
      invoiceNumber: searchInvoiceNumber ? Number(searchInvoiceNumber) : undefined,
      clientId: selectedClientId || undefined,
      clientName: searchTerm || undefined,
      invoiceType: selectedInvoiceType || undefined,
      currency: selectedCurrency || undefined,
      paymentMethod: selectedPaymentMethod || undefined,
      page: page,
      size: pageSize
    });
  }

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await search(0);
      setInvoices(response.content);
      setHasMore(!response.last);
      setCurrentPage(0);
    } catch (err: any) {
      console.error('Error loading invoices:', err);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreInvoices = async () => {
    if (!selectedCompany.selectedCompanyId || isLoadingMore || !hasMore) return;

    try {
      setIsLoadingMore(true);
      const response = await search(currentPage);
      setInvoices(prev => [...prev, ...response.content]);
      setHasMore(!response.last);
    } catch (err) {
      console.error('Error loading more invoices:', err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const loadPaymentMethods = () => {
    const methods = Object.values(PaymentMethod);
    setPaymentMethods(methods as PaymentMethod[]);
  };

  const loadInvoiceTypes = async () => {
    const types = Object.values(InvoiceType)
    setInvoiceTypes(types as InvoiceType[]);
  };

  const handleGeneratePDF = async (invoiceId: string) => {
    try {
      setGeneratingPDF(invoiceId);

      // Get the invoice to find the company ID
      const invoice = invoices.find(inv => inv.id === invoiceId);
      if (!invoice) {
        throw new Error('Invoice not found');
      }

      // Get company information to find the template ID
      const company = selectedCompany.selectedCompany;
      if (!company || !company.templateId) {
        throw new Error('No template selected for this company');
      }

      // Generate PDF using template and download it
      await templateAPI.generatePDFBytesAndOpen(company.templateId, invoice);

    } catch (err: any) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF. Please make sure a template is selected for this company.');
    } finally {
      setGeneratingPDF(null);
    }
  };

  const handleViewPDF = async (invoiceId: string) => {
    try {
      setViewingPDF(invoiceId);

      // Get the invoice to find the company ID
      const invoice = invoices.find(inv => inv.id === invoiceId);
      if (!invoice) {
        throw new Error('Invoice not found');
      }

      // Get company information to find the template ID
      const company = selectedCompany.selectedCompany;
      if (!company) {
        throw new Error('No template selected for this company');
      }

      // Generate PDF using template and open in new tab
      await templateAPI.openPDFInNewTab(company.templateId, invoice);

    } catch (err: any) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF. Please make sure a template is selected for this company.');
    } finally {
      setViewingPDF(null);
    }
  };

  const handleCopyLink = async (invoice: Invoice) => {
    try {
      setCopyingLink(invoice.id);
      let generationId = invoice.generationId;
      if (!generationId) {
        // Generate link if not exists
        const publicData = {
          clientName: invoice.clientName,
          clientIce: invoice.clientIce,
          clientStreet: invoice.clientStreet,
          clientCity: invoice.clientCity,
          clientPostalCode: invoice.clientPostalCode,
          clientCountry: invoice.clientCountry
        };
        const updatedInvoice = await invoiceAPI.generateLink(invoice.id, publicData);
        generationId = updatedInvoice.generationId;
        // Update local state
        setInvoices(invoices.map(inv => inv.id === invoice.id ? updatedInvoice : inv));
      }

      if (generationId) {
        const baseUrl = window.location.origin;
        const publicUrl = `${baseUrl}/preview/${generationId}`;
        await navigator.clipboard.writeText(publicUrl);
        setCopiedLink(invoice.id);
        setTimeout(() => setCopiedLink(null), 2000);
      }
    } catch (err) {
      console.error('Error copying link:', err);
      setError('Failed to generate or copy link');
    } finally {
      setCopyingLink(null);
    }
  };

  const handleInvoiceTypeSelect = (invoiceType: string) => {
    // Navigate to create page with the selected invoice type
    navigate(`/invoices/create?type=${encodeURIComponent(invoiceType)}`);
  };

  const handleSearch = async () => {
    loadInvoices();
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) {
      return;
    }

    try {
      await invoiceAPI.deleteInvoice(invoiceId);
      setInvoices(invoices.filter(invoice => invoice.id !== invoiceId));
    } catch (err: any) {
      console.error('Error deleting invoice:', err);
      setError('Failed to delete invoice. Please try again.');
    }
  };

  const handleConvertToInvoice = (invoice: Invoice) => {
    const initialData = {
      ...invoice,
      id: undefined, // Clear ID to create new
      invoiceNumber: undefined, // Clear number to generate new
      invoiceType: InvoiceType.SELL_INVOICE,
      creationDate: new Date().toISOString().split('T')[0], // Set to today
      orderId: undefined,
    };
    navigate('/invoices/create', { state: { initialData, originalQuoteId: invoice.id } });
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const applyFilters = () => {
    loadInvoices();
  };

  const clearFilters = () => {
    setSelectedPaymentMethod('');
    setSelectedInvoiceType('');
    setSelectedCurrency('');
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setStartDeadlineDate('');
    setEndDeadlineDate('');
    setSearchInvoiceNumber('');
    setSelectedClientId('');
    setCurrentPage(0);

    if (selectedCompany.selectedCompanyId) {
      setLoading(true);
      invoiceAPI.searchInvoicesAdvanced({
        companyId: selectedCompany.selectedCompanyId,
        page: 0,
        size: pageSize
      })
        .then(response => {
          setInvoices(response.content);
          setHasMore(!response.last);
          setLoading(false);
        })
        .catch(err => {
          setError('Failed to reset filters.');
          setLoading(false);
        });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'MAD',
    }).format(amount);
  };

  // Show message if no company is selected
  if (!selectedCompany.selectedCompanyId) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('clients.noCompanySelected')}</h2>
            <p className="text-gray-600 mb-4">{t('clients.noCompanyMessage')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`min-h-screen ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
            <button
              onClick={() => navigate('/dashboard')}
              className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} text-gray-600 hover:text-gray-900 transition-colors`}
            >
              <ArrowLeft className={`h-5 w-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />

              <span>{t('invoices.backToDashboard')}</span>
            </button>
          </div>
          <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
            <DollarSign className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">{t('invoices.title')}</h1>
          </div>
        </div>

        {/* Search and Filters */}
        <div id="invoice-list-search-bar" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10`} />
                <input
                  type="text"
                  placeholder={t('invoices.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
              </div>
            </div>

            {/* Filters Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors`}
            >
              <Filter className="w-4 h-4" />
              <span>{t('invoices.filters')}</span>
            </button>

            {/* Export Button */}

            {/* Create Invoice Button */}
            <button
              id="invoice-list-create-btn"
              onClick={() => setShowInvoiceTypeModal(true)}
              className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors`}
            >
              <Plus className="w-4 h-4" />
              <span>{t('invoices.createInvoice')}</span>
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {t('invoices.startDate')}
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {t('invoices.endDate')}
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {t('invoices.startDeadlineDate')}
                  </label>
                  <input
                    type="date"
                    value={startDeadlineDate}
                    onChange={(e) => setStartDeadlineDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {t('invoices.endDeadlineDate')}
                  </label>
                  <input
                    type="date"
                    value={endDeadlineDate}
                    onChange={(e) => setEndDeadlineDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {t('invoices.invoiceNumber')}
                  </label>
                  <input
                    type="number"
                    value={searchInvoiceNumber}
                    onChange={(e) => setSearchInvoiceNumber(e.target.value)}
                    placeholder="e.g. 1001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {t('invoices.client')}
                  </label>
                  <select
                    value={selectedClientId}
                    onChange={(e) => setSelectedClientId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('invoices.allClients')}</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {t('invoices.supplier')}
                  </label>
                  <select
                    value={selectedClientId}
                    onChange={(e) => setSelectedClientId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('invoices.allSuppliers')}</option>
                    {suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {t('invoices.paymentMethod')}
                  </label>
                  <select
                    value={selectedPaymentMethod}
                    onChange={(e) => setSelectedPaymentMethod(e.target.value as PaymentMethod | '')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('invoices.allPaymentMethods')}</option>
                    {paymentMethods.map((method) => (
                      <option key={method} value={method}>
                        {t(`common.payments.${method}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {t('invoices.invoiceType')}
                  </label>
                  <select
                    value={selectedInvoiceType}
                    onChange={(e) => setSelectedInvoiceType(e.target.value as InvoiceType | '')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('invoices.allTypes')}</option>
                    {invoiceTypes.map((type) => (
                      <option key={type} value={type}>
                        {t(`invoiceEdit.types.${type}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {t('invoices.currency')}
                  </label>
                  <select
                    value={selectedCurrency}
                    onChange={(e) => setSelectedCurrency(e.target.value as Currency | '')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('invoices.allCurrencies')}</option>
                    {Object.values(Currency).map((currency) => (
                      <option key={currency} value={currency}>
                        {currency} – {t('companies.currency'+currency.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {t('invoices.itemsPerPage')}
                  </label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              {/* Filter Actions */}
              <div className={`mt-4 flex justify-end ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('invoices.clearFilters')}
                </button>
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('invoices.applyFilters')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Invoices Table */}
        <div id="invoice-list-table" className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('invoices.noInvoicesFound')}</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm ? t('invoices.noInvoicesMatchSearch') : t('invoices.getStartedCreateInvoice')}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowInvoiceTypeModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  {t('invoices.createInvoice')}
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                        {t('invoices.invoice')}
                      </th>
                      <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                        {t('invoices.client')}
                      </th>
                      <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                        {t('invoices.date')}
                      </th>
                      <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                        {t('invoices.deadline')}
                      </th>
                      <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                        {t('invoices.amount')}
                      </th>
                      <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                        {t('invoices.paymentMethod')}
                      </th>
                      <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                        {t('invoices.type')}
                      </th>
                      <th className={`px-6 py-3 ${isRTL ? 'text-left' : 'text-right'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                        {t('invoices.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {invoice.formattedInvoiceNumber}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{invoice.clientName}</div>
                          {invoice.clientNumber && (
                            <div className="text-sm text-gray-500">#{invoice.clientNumber}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(invoice.creationDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.deadlineDate ? formatDate(invoice.deadlineDate) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(invoice.order?.totalAmount || 0, invoice.currency)}
                          </div>
                          {(() => {
                            const discountAmount = invoice.order?.discountAmount || 0;
                            return discountAmount > 0 && (
                              <div className="text-xs text-gray-500">
                                {t('invoices.discount')}: {formatCurrency(discountAmount, invoice.currency)}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {t(`common.payments.${invoice.paymentMethod}`)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {t(`invoiceEdit.types.${invoice.invoiceType}`)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className={`flex items-center justify-end ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                            <button
                              onClick={() => {
                                setSelectedInvoiceId(invoice.id);
                                setShowPaymentModal(true);
                              }}
                              className="text-green-600 hover:text-green-900"
                              title={t('payment.addPayment')}
                            >
                              <DollarSign className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleViewPDF(invoice.id)}
                              className="text-blue-600 hover:text-blue-900"
                              title={t('invoices.viewInvoice')}
                            >
                              {viewingPDF === invoice.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleCopyLink(invoice)}
                              className="text-gray-600 hover:text-gray-900"
                              title={t('invoices.copyPublicLink')}
                              disabled={!!copyingLink}
                            >
                              {copiedLink === invoice.id ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : copyingLink === invoice.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                              ) : (
                                <LinkIcon className="w-4 h-4" />
                              )}
                            </button>
                            {invoice.invoiceType === 'QUOTE' && !invoice.convertedToInvoiceId && (
                              <button
                                onClick={() => handleConvertToInvoice(invoice)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title={t('invoices.convertToInvoice')}
                              >
                                <FileCheck className="w-4 h-4" />
                              </button>
                            )}
                            <div className="relative group">
                              <button
                                disabled={generatingPDF === invoice.id}
                                className="text-purple-600 hover:text-purple-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                title={t('invoices.downloadPDF')}
                                onClick={() => handleGeneratePDF(invoice.id)}
                              >
                                {generatingPDF === invoice.id ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                                ) : (
                                  <Download className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                            <button
                              onClick={() => navigate(`/invoices/${invoice.id}/edit`)}
                              className="text-green-600 hover:text-green-900"
                              title={t('invoices.editInvoice')}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteInvoice(invoice.id)}
                              className="text-red-600 hover:text-red-900"
                              title={t('invoices.deleteInvoice')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Infinite Scroll Trigger */}
              {hasMore && (
                <div className="bg-white px-4 py-3 flex items-center justify-center border-t border-gray-200 sm:px-6">
                  <div ref={setLoadMoreTriggerRef} className="w-full h-1">
                    {isLoadingMore && (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-gray-700">{t('common.loading')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Invoice Type Selection Modal */}
        <InvoiceTypeModal
          isOpen={showInvoiceTypeModal}
          onClose={() => setShowInvoiceTypeModal(false)}
          onSelect={handleInvoiceTypeSelect}
          invoiceTypes={invoiceTypes}
        />
      </div>
      {/* Add Payment Modal */}
      {selectedInvoiceId && (
        <AddPaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedInvoiceId(null);
          }}
          invoiceId={selectedInvoiceId}
          onSuccess={() => {
            // Refresh list to update status if needed
            loadInvoices();
          }}
        />
      )}

      {/* Onboarding Help Button */}
      <button
        type="button"
        className="onboarding-help-btn"
        title={t('onboarding.replayTour')}
        onClick={() => manualStart({
          tourId: 'invoice-list',
          steps: [
            {
              element: '#invoice-list-create-btn',
              popover: {
                title: t('onboarding.invoiceList.createBtnTitle'),
                description: t('onboarding.invoiceList.createBtnDesc'),
                side: 'bottom' as const,
                align: 'end' as const,
              },
            },
            {
              element: '#invoice-list-search-bar',
              popover: {
                title: t('onboarding.invoiceList.searchTitle'),
                description: t('onboarding.invoiceList.searchDesc'),
                side: 'bottom' as const,
                align: 'start' as const,
              },
            },
            {
              element: '#invoice-list-table',
              popover: {
                title: t('onboarding.invoiceList.tableTitle'),
                description: t('onboarding.invoiceList.tableDesc'),
                side: 'top' as const,
                align: 'start' as const,
              },
            },
          ],
        })}
      >
        <HelpCircle size={22} />
      </button>
    </Layout>
  );
};

export default InvoiceListPage;
